import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "./logo.png"; // make sure this path is correct

export default function Header() {
  const [open, setOpen] = useState(false);           // dropdown open state
  const [focusIndex, setFocusIndex] = useState(-1);  // keyboard focus in dropdown
  const menuRef = useRef(null);
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
        setFocusIndex(-1);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setOpen(false);
    setFocusIndex(-1);
  }, [location.pathname]);

  const solutions = [
    { label: "Energy Management", to: "/solutions/energy-management" },
    //{ label: "Smart Meter", to: "/solutions/smart-meter" },
    //{ label: "Diesel Monitor", to: "/solutions/diesel-monitor" },
    { label: "UPS Monitor", to: "/solutions/ups-monitor" },
    { label: "Agriculture Monitor", to: "/solutions/agriculture-monitor" },
  ];

  function onKeyDown(e) {
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault();
      setOpen(true);
      setFocusIndex(0);
      return;
    }

    if (open) {
      if (e.key === "Escape") {
        setOpen(false);
        setFocusIndex(-1);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIndex((i) => Math.min(i + 1, solutions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Tab") {
        // let Tab behave normally but close dropdown if tabbing away
        // do nothing here; it will close on route change or outside click
      }
    }
  }

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-[#0b1220]/90 backdrop-blur-md text-white shadow-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Left: Logo and Name */}
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="Power Sense" className="w-10 h-10 rounded-md bg-white p-1" />
              <div className="leading-tight">
                <div className="text-sm font-semibold tracking-wide text-orange-400">Power Sense</div>
                <div className="text-xs text-slate-300">IoT • Smart Energy</div>
              </div>
            </Link>
          </div>

          {/* Right: Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {/* Solutions (dropdown) */}
            <div className="relative" ref={menuRef}>
              <button
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => { setOpen((v) => !v); setFocusIndex(0); }}
                onKeyDown={onKeyDown}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => { /* keep open until mouse leaves dropdown container below */ }}
                className="flex items-center gap-2 px-2 py-1 text-sm font-medium hover:text-orange-300 focus:outline-none"
              >
                <span>Solutions</span>
                <svg className={`w-4 h-4 transform transition-transform ${open ? "rotate-180" : "rotate-0"}`} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Dropdown panel */}
              <div
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
                role="menu"
                aria-label="Solutions"
                className={`origin-top-left absolute left-0 mt-2 w-56 bg-white text-slate-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50 transition-opacity ${open ? "opacity-100 visible" : "opacity-0 invisible"} `}
              >
                <div className="py-2">
                  {solutions.map((s, idx) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      role="menuitem"
                      tabIndex={open ? 0 : -1}
                      className={`block px-4 py-3 text-sm hover:bg-slate-100 ${focusIndex === idx ? "bg-slate-100" : ""}`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setOpen(false);
                        }
                      }}
                      onFocus={() => setFocusIndex(idx)}
                    >
                      {s.label}
                    </Link>
                  ))}

                </div>
              </div>
            </div>

            {/* Other nav items */}
            
            <Link to="/deployment" className="text-sm hover:text-orange-300">Deployment</Link>
            <Link to="/Live-Monitoring" className="text-sm hover:text-orange-300">Live Monitoring</Link>
            <Link to="/training" className="text-sm hover:text-orange-300">Training</Link>
            <Link to="/Careers" className="text-sm hover:text-orange-300">Careers</Link>
            <Link to="/Aboutus" className="text-sm hover:text-orange-300">About Us</Link>
          </nav>

          {/* Mobile menu toggle */}
          <div className="md:hidden">
            {/* simple mobile menu button: implement mobile drawer if needed */}
            <button
              className="p-2 rounded-md hover:bg-white/5 focus:outline-none"
              onClick={() => {
                // for simplicity, open the solutions route on mobile; you can replace with drawer logic
                window.location.href = "/solutions";
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

        </div>
      </div>
    </header>
  );
}
